import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../components/Button/Button';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { selectProject, selectResults } from '../../../selector/conversion';
import Canvas, { IInspectMaterial } from './Canvas';
import './ChannelDesign.scss';
import gridImage from '../../../../images/grid.svg';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { roundToFour, roundToTwo } from '../../InputForm/Geometry/utils';
import { ISubSectorResponse } from '../../../api/IApiResponse';
import { isConcrete } from '../../../../enum/Material';

const defaultScale = 70;
const minScale = 0;

const defaultOffset = 125;

let inspectMaterialDefault: IInspectMaterial = {
    color: '',
    materialName: '',
    length: 0,
    allowedShearStress: 0,
    roughness: 0,
    isGeotextile: false,
    category: undefined,
};

const ChannelDesign = () => {
    let mousePosition;
    let offset = [0, 0];
    let isDown = false;

    const projectInfo = useAppSelector(selectProjectInfo);

    const { t } = useTranslation();

    const [showTable, setShowTable] = React.useState<boolean>(false);
    const [inspectMaterial, setInspectMaterial] = React.useState<IInspectMaterial>(inspectMaterialDefault);

    const canvasEl = React.useRef<HTMLDivElement>(null);

    const result = useAppSelector(selectResults);
    const project = useAppSelector(selectProject);

    const [scale, setScale] = React.useState<number>(defaultScale);

    const handleZoom = (offset: number) => {
        let _scale = scale + offset * -0.1;
        if (_scale > minScale) {
            setScale(_scale);
        }
    };

    const handleOnClickSector = (sec: IInspectMaterial) => {
        setInspectMaterial(sec);
        setShowTable(true);
    };

    const handleAutoscale = () => {
        let width = 0;
        let height = 0;
        let scaleWidth = 0;
        let scaleHeight = 0;
        let offsetHeight = 4;
        let offsetWidth = 6;
        let sec = _sectors!.filter(x => x.isActive);
        sec.forEach(x => {
            width += x.width;
            height += x.height;
        });
        scaleWidth = canvasEl.current!.clientWidth / (width + offsetWidth);
        scaleHeight = canvasEl.current!.clientHeight / (height + offsetHeight);
        setScale(Math.min(scaleWidth, scaleHeight));
    };

    React.useEffect(() => {
        canvasEl.current!.addEventListener(
            'mousedown',
            e => {
                isDown = true;
                offset = [canvasEl.current!.offsetLeft - e.clientX, canvasEl.current!.offsetTop - e.clientY];
            },
            true,
        );

        canvasEl.current!.addEventListener(
            'mouseup',
            e => {
                isDown = false;
            },
            true,
        );

        canvasEl.current!.addEventListener(
            'mousemove',
            e => {
                e.preventDefault();
                if (isDown) {
                    mousePosition = {
                        x: e.clientX,
                        y: e.clientY,
                    };
                    canvasEl.current!.style.left = mousePosition.x + offset[0] + 'px';
                    canvasEl.current!.style.top = mousePosition.y + offset[1] + 'px';
                }
            },
            true,
        );
    }, []);

    React.useEffect(() => handleAutoscale(), [result.data]);

    let _freeboard = result.data?.generalResult.general.freeboard;
    let _waterDepth = result.data?.generalResult.general.waterDepth;
    let _sectors = project.data?.geometry!.map(sec => {
        let materialResults = result.data?.materialsResult.find(mat => mat.channel === sec.channel && !mat.subSectorPosition);
        let subSec: ISubSectorResponse[] = [];
        if (!!sec.subSectors && sec.subSectors?.length > 0) {
            subSec = sec.subSectors.map(sub => {
                let materialResults = result.data?.materialsResult.find(mat => mat.channel === sec.channel && sub.position === mat.subSectorPosition);
                return {
                    ...sub,
                    error: !isConcrete(materialResults?.category) && (materialResults?.isDangerousShearStress || materialResults?.isDangerousVelocityUnderLining),
                };
            });
        }
        return {
            ...sec,
            subSectors: subSec,
            error: !isConcrete(materialResults?.category) && (materialResults?.isDangerousShearStress || materialResults?.isDangerousVelocityUnderLining),
        };
    });

    return (
        <>
            <div onWheel={e => handleZoom(e.deltaY)} className='canvas-container' style={{ background: `repeat url(${gridImage})` }}>
                <div className='position-absolute w-100 d-flex align-items-center justify-content-center h-100' ref={canvasEl}>
                    <Canvas scale={scale} waterDepth={_waterDepth} freeboard={_freeboard} sectors={_sectors!} onClickSector={handleOnClickSector} />
                </div>
            </div>
            <div className={`inspect-table-container ${showTable ? '' : 'd-none'}`}>
                <div className='d-flex justify-content-between cursor-pointer' onClick={() => setShowTable(false)}>
                    <div className='d-flex align-items-center'>
                        <span>
                            <b>{t('canvas_table_material')}</b>
                        </span>
                        <div className='squareColor' style={{ backgroundColor: inspectMaterial?.color }}></div>
                    </div>
                    <i className='fa-solid fa-xmark'></i>
                </div>
                <div className='d-flex flex-column'>
                    <span>
                        {t('canvas_table_name')} {inspectMaterial?.materialName}
                    </span>
                    <span>
                        {t('canvas_table_length', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })} {roundToTwo(inspectMaterial?.length)}
                    </span>
                    <span>
                        {t('canvas_table_shear_stress', { unit_measure: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number] })} {isConcrete(inspectMaterial?.category) ? '—' : roundToTwo(inspectMaterial?.allowedShearStress)}
                    </span>
                    <span>
                        {t('canvas_table_roughness')} {roundToFour(inspectMaterial?.roughness)}
                    </span>
                    {inspectMaterial.isGeotextile && (
                        <div className='d-flex'>
                            <div className='geotextile-line'></div>
                            {t('canvas_table_geotextile')}
                        </div>
                    )}
                </div>
            </div>
            <div className='action-canvas-container'>
                <Button onClick={() => handleZoom(-defaultOffset)}>
                    <i className='fa-solid fa-plus fa-lg' />
                </Button>
                <Button onClick={() => handleZoom(+defaultOffset)}>
                    <i className='fa-solid fa-minus fa-lg' />
                </Button>
                <Button onClick={handleAutoscale}>
                    <i className='fa-solid fa-arrows-left-right-to-line fa-sm' />
                </Button>
            </div>
        </>
    );
};

export default ChannelDesign;
